import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import Swal from 'sweetalert2';
import { ExperienceTeamCheckRowDef } from 'src/components/common/rows/ExperienceTeamCheckRow';
import experienceService from 'src/services/api/experienceService';
import { Tab, Select, Button, Input, Checkbox } from 'semantic-ui-react';
import { chPerm, formSelectService, scrollUp, searchSelectProps } from 'src/components/helpers';
import eventService from 'src/services/api/eventService';
import styled from '@emotion/styled';
import { VolunteersBirthdaysModal } from 'src/containers/volunteer/components/volunteersBirthdays';
import AGTable from 'src/components/table/aggrid';
import { ListsContext } from 'src/services/contextProviders/lists/listsContext';
import { TableContainerComponent } from 'src/assets/styles/components/Table';
import { AdminTableSection } from 'src/assets/styles/components/AdminTable';
import {
  isCaptchaRequired,
  useRecaptchaToken,
  RECAPTCHA_SECURITY_ERROR,
} from 'src/components/helpers/recaptchaUtils';

const ScrollUpComponent = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  .button {
    width: 20px;
    border-radius: 50%;
    height: 60px;
    display: flex;
    align-content: center;
    align-items: center;
    justify-content: center;
  }
`;

const ConfirmedVolunteersCheck = (props) => {
  const [volunteersByEvent, set_volunteersByEvent] = useState([]);
  const [inputs, setInputs] = useState({ eventSlugname: 'Auditorio_Principal' });
  const [previousEventsNames, set_previousEventsNames] = useState([]);
  const [loading, set_loading] = useState([]);
  const [showImgs, set_showImgs] = useState(true);
  const [showArea, set_showArea] = useState(false);
  const [showCedNRole, set_showCedNRole] = useState(false);
  const [filterAttendance, setFilterAttendance] = useState(false);
  const [filterSnack, setFilterSnack] = useState(false);
  const gridApi = useRef(null);
  const { churchMinistries } = useContext(ListsContext);
  const getCaptchaToken = useRecaptchaToken('volunteer_service_logs');

  const eventSlugname = inputs.eventSlugname;

  useEffect(() => {
    getVolunteersByEvent();
  }, [eventSlugname]);

  useEffect(() => {
    getPreviousEventNames();
  }, []);

  useEffect(() => {
    getVolunteersByEvent();
  }, [inputs.serviceArea]);

  useEffect(() => {
    gridApi?.current?.api?.setQuickFilter(inputs.keyword);
  }, [inputs.keyword]);

  const getVolunteersByEvent = async () => {
    set_loading?.(true);

    const { eventSlugname, serviceArea } = inputs;

    const captchaRequired = isCaptchaRequired();
    const captcha = await getCaptchaToken({ skip: !captchaRequired });
    if (captchaRequired && !captcha) {
      set_loading?.(false);
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      return;
    }

    experienceService
      .getAllVolunteerServiceLogs({
        eventSlugname,
        serviceArea,
        startDate: moment().tz('America/Bogota').startOf('day')?.toDate(),
        endDate: moment().tz('America/Bogota').endOf('day')?.toDate(),
        ...(captchaRequired ? { captcha } : {}),
      })
      .then((response) => {
        set_loading(false);
        if (response.data.success) {
          set_volunteersByEvent(response.data.volunteersByEvent);
        } else if (response.data.message) {
          Swal.fire(response.data.message);
        }
      })
      .catch((err) => {
        set_loading?.(false);
        if (err?.response?.data?.message) Swal.fire(err.response.data.message);
      });
  };

  const getPreviousEventNames = () => {
    set_loading(true);
    eventService
      .getEventsNames({ postDays: 3 })
      .then((response) => {
        set_loading(false);
        if (response.data.success) {
          set_previousEventsNames(response.data.eventsNames);
        }
      })
      .catch((err) => {
        set_loading(false);
      });
  };

  const selectHandle = (a, { value, name }) => {
    setInputs({ ...inputs, [name]: value });
  };

  const onEnter = (e) => {
    if (e.key === 'Enter') getVolunteersByEvent();
  };

  const cleanFilters = () => {
    let newInputs = { ...inputs, keyword: '' };
    if (inputs.serviceArea) {
      newInputs = { ...newInputs, serviceArea: '' };
    }
    setInputs({ ...newInputs, serviceArea: '' });
    setFilterAttendance(false);
    setFilterSnack(false);
  };

  const filterVolunteers = (eventVolunteers) =>
    uniqueVolunteers(eventVolunteers)?.filter((volunteer) => {
      if (filterAttendance && volunteer.attendance) return false;
      if (filterSnack && volunteer.snack) return false;
      return true;
    });

  const listOfVolunteersWithBdDte = () => {
    const volunteers = [];
    volunteersByEvent.forEach((service) => {
      service.EventVolunteers.forEach((data) => {
        volunteers.push(data.Volunteer);
      });
    });
    return volunteers;
  };

  const uniqueVolunteers = useCallback((eventVolunteers) =>
    eventVolunteers?.reduce((acc, volunteer) => {
      if (!acc.some((vol) => vol?.Volunteer?.guest_id === volunteer?.Volunteer?.guest_id)) {
        acc.push(volunteer);
      }
      return acc;
    }, [])
  );

  return (
    <AdminTableSection>
      <h2>Voluntarios confirmados</h2>
      <div>
        {formSelectService({
          Select,
          label: 'Proximo evento a servir',
          name: 'eventSlugname',
          handler: (a, { value }) => {
            if (value === 'Servicio_del_fin_de_semana') value = 'Auditorio_Principal';
            setInputs({ ...inputs, eventSlugname: value });
          },
          value: eventSlugname,
          options: ['Servicio_del_fin_de_semana', ...previousEventsNames],
        })}
        <div>
          <Select
            placeholder="Selecciona el area para consultar."
            style={{
              display: 'inline-block',
              margin: ' 7px 0',
              width: '45%',
            }}
            loading={loading}
            {...searchSelectProps({
              name: 'serviceArea',
              value: inputs.serviceArea,
              handler: selectHandle,
              options: [
                { key: '', value: '', text: 'Todos' },
                ...churchMinistries.map((opt) => ({
                  key: opt?.key,
                  value: opt?.key,
                  text: opt?.name,
                })),
              ],
            })}
          />
        </div>
        <div>
          <Button primary onClick={() => set_showImgs(!showImgs)}>
            {showImgs ? 'Ocultar' : 'Mostrar'} fotos
          </Button>
          <Button primary onClick={() => set_showCedNRole(!showCedNRole)}>
            {showCedNRole ? 'Ocultar' : 'Mostrar'} cédula y rol
          </Button>
          <Button primary onClick={() => set_showArea(!showArea)}>
            {showArea ? 'Ocultar' : 'Mostrar'} area
          </Button>
          <VolunteersBirthdaysModal
            daysRange={7}
            volunteers={listOfVolunteersWithBdDte()}
            onOpen={cleanFilters}
            showArea
          />
        </div>
      </div>
      <div style={{ margin: '20px 0 0 0' }}>
        <Tab
          panes={[
            ...volunteersByEvent.map((service) => {
              const totalSnack =
                uniqueVolunteers(service?.EventVolunteers)?.filter((volunteer) => volunteer.snack)
                  .length || 0;
              const totalAttendance =
                uniqueVolunteers(service?.EventVolunteers)?.filter(
                  (volunteer) => volunteer.attendance
                ).length || 0;

              return {
                menuItem: moment(service?.date).format('dddd, DD [de] MMM'),
                render: () => (
                  <Tab.Pane attached={false} key={service.id}>
                    <TableContainerComponent>
                      <table style={{ width: '40%', textAlign: 'center' }}>
                        <thead>
                          <tr>
                            <th>Total voluntarios</th>
                            {chPerm(['experienceCoord']) && <th>Con entrada</th>}
                            {chPerm(['salaExp']) && <th>Con refrigerio</th>}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{uniqueVolunteers(service?.EventVolunteers)?.length}</td>
                            {chPerm(['experienceCoord']) && <td>{totalAttendance}</td>}
                            {chPerm(['salaExp']) && <td>{totalSnack}</td>}
                          </tr>
                          <tr>
                            <td>Filtrar por faltantes</td>
                            {chPerm(['experienceCoord']) && (
                              <td>
                                <Checkbox
                                  toggle
                                  checked={filterAttendance}
                                  onClick={() => setFilterAttendance((prev) => !prev)}
                                />
                              </td>
                            )}
                            {chPerm(['salaExp']) && (
                              <td>
                                <Checkbox
                                  toggle
                                  checked={filterSnack}
                                  onClick={() => setFilterSnack((prev) => !prev)}
                                />
                              </td>
                            )}
                          </tr>
                        </tbody>
                      </table>
                    </TableContainerComponent>
                    <Input
                      loading={loading}
                      name={'keyword'}
                      onChange={(e) => setInputs({ ...inputs, keyword: e.target.value })}
                      value={inputs.keyword}
                      onKeyPress={onEnter}
                      style={{ width: '45%', margin: '20px 0' }}
                      placeholder="Buscar..."
                    />
                    <Button color="black" onClick={cleanFilters}>
                      Limpiar
                    </Button>
                    <AGTable
                      rowData={filterVolunteers(service?.EventVolunteers)}
                      rowHeight={90}
                      tableRef={gridApi}
                      columnDefs={ExperienceTeamCheckRowDef({
                        loading,
                        service,
                        eventSlugname,
                        getVolunteersByEvent,
                        set_loading,
                        showImgs,
                        showCedNRole,
                        showArea,
                      })}
                      defaultCol={{
                        suppressMovable: true,
                      }}
                    />
                  </Tab.Pane>
                ),
              };
            }),
          ]}
        />
        <ScrollUpComponent>
          <Button color="black" onClick={scrollUp}>
            <span>↑</span>
          </Button>
        </ScrollUpComponent>
      </div>
    </AdminTableSection>
  );
};

export default ConfirmedVolunteersCheck;
